import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as CustomerActions from './customer.actions';
import { catchError, concatMap, exhaustMap, map, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Customer } from '../../../core/models/customer.model';
import { CustomerService } from '../../../core/services/customer.service';
import { Administrator } from '../../../core/models/administrator.model';
import { CustomerAdminService } from '../../../core/services/customer-admin.service';
import { NotificationService } from '../../../core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CustomerEffects {
  customerPrefix = 'SUPERUSER.CUSTOMER.NOTIFICATIONS.';
  administratorPrefix = 'MANAGER.ADMINISTRATORS.NOTIFICATIONS.';

  constructor(
    private actions$: Actions,
    private customerService: CustomerService,
    private router: Router,
    private store$: Store,
    private administratorService: CustomerAdminService,
    private notification: NotificationService,
    private translate: TranslateService
  ) {}

  // Resolve notification text when the notification is shown, not on a timer at
  // construction.  Effects are built before the app selects a language, so a
  // pre-loaded cache is empty during that window - and reading from it threw,
  // which catchError then reported as a failed save.  instant() falls back to
  // the key, so the worst case is an untranslated message, not a lost save.
  private message(token: string): string {
    return this.translate.instant(token);
  }

  getCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.getCustomers),
      concatMap((action) =>
        this.customerService.getCustomers().pipe(
          map((customers: Customer[]) => {
            return CustomerActions.getCustomersSuccess({ customers });
          }),
          catchError((error) =>
            of(CustomerActions.getCustomersFailure({ error }))
          )
        )
      )
    )
  );

  createCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.createCustomer),
      concatMap((action) => {
        this.notification.showProgressDialog();
        return this.customerService.createCustomer(action.customer).pipe(
          map((customer: Customer) => {
            this.notification.closeProgressDialog();
            this.notification.showSnackBar(
              this.message(`${this.customerPrefix}CREATED_SUCCESS`)
            );
            return CustomerActions.createCustomerSuccess({ customer });
          }),
          catchError((error) => {
            this.notification.closeProgressDialog();
            return of(CustomerActions.createCustomerFailure({ error }));
          })
        );
      })
    )
  );

  updateCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateCustomer),
      concatMap((action) => {
        this.notification.showProgressDialog();
        return this.customerService.updateCustomer(action.customer).pipe(
          // Carry the API's saved customer, not the request payload.  The form
          // posts a subset of the customer, so echoing it back would drop the
          // server-owned fields the reducer writes into the customer list.
          map((customer: Customer) => {
            this.notification.closeProgressDialog();
            this.notification.showSnackBar(
              this.message(`${this.customerPrefix}UPDATED_SUCCESS`)
            );
            return CustomerActions.updateCustomerSuccess({ customer });
          }),
          catchError((error) => {
            this.notification.closeProgressDialog();
            return of(CustomerActions.updateCustomerFailure({ error }));
          })
        );
      })
    )
  );

  deleteCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.deleteCustomer),
      concatMap((action) => {
        this.notification.showProgressDialog();
        return this.customerService.deleteCustomer(action.customer).pipe(
          map(() => {
            this.notification.closeProgressDialog();
            this.notification.showSnackBar(
              this.message(`${this.customerPrefix}DELETED_SUCCESS`)
            );
            return CustomerActions.deleteCustomerSuccess({
              customer: action.customer,
            });
          }),
          catchError((error) => {
            this.notification.closeProgressDialog();
            return of(CustomerActions.deleteCustomerFailure({ error }));
          })
        );
      })
    )
  );

  updateCustomerSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.updateCustomerSettings),
      concatMap((action) => {
        this.notification.showProgressDialog();
        return this.customerService.updateCustomer(action.customer).pipe(
          // Carry the API's saved customer, not the request payload.  Echoing
          // the request back reports whatever we asked for as saved, hiding
          // fields the server ignored until the next reload.
          map((customer: Customer) => {
            this.notification.closeProgressDialog();
            this.notification.showSnackBar(
              this.translate.instant('MANAGER.CUSTOMER_SETTINGS.SAVED')
            );
            return CustomerActions.updateCustomerSettingsSuccess({ customer });
          }),
          catchError((error) => {
            this.notification.closeProgressDialog();
            return of(CustomerActions.updateCustomerSettingsFailure({ error }));
          })
        );
      })
    )
  );

  createUpdateCustomerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CustomerActions.createCustomerSuccess,
          CustomerActions.updateCustomerSuccess
        ),
        tap((action) => this.router.navigate(['/superuser/customers']))
      ),
    { dispatch: false }
  );

  refreshCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.refreshCustomer),
      exhaustMap((action) =>
        this.customerService.getCustomer(action.id).pipe(
          map((customer: Customer) => {
            return CustomerActions.refreshCustomerSuccess({ customer });
          }),
          catchError((error) =>
            of(CustomerActions.refreshCustomerFailure({ error }))
          )
        )
      )
    )
  );

  createAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomerActions.createAdministrator),
      concatMap((action) => {
        this.notification.showProgressDialog();
        return this.administratorService
          .createAdministrator(action.administrator)
          .pipe(
            map((administrator: Administrator) => {
              administrator.password = action.administrator.password;
              administrator.permissions = {
                ...action.administrator.permissions,
                customer_admin_id: administrator.id,
                sites: [],
              };
              this.notification.closeProgressDialog();
              this.notification.showSnackBar(
                this.message(`${this.administratorPrefix}CREATED_SUCCESS`)
              );
              return CustomerActions.createAdministratorSuccess({
                administrator,
              });
            }),
            catchError((error) => {
              this.notification.closeProgressDialog();
              return of(CustomerActions.createAdministratorFailure({ error }));
            })
          );
      })
    )
  );

  createUpdateAdministratorSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomerActions.createAdministratorSuccess),
        tap((action) => this.router.navigate(['/superuser/customers/list']))
      ),
    { dispatch: false }
  );
}
